import { message } from 'antd';
import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import { hydrateCollaboratorUsers } from '@/utils/collaborators';
import { CoreEnums } from '@/utils/domain/constants';
import { isJsonLike, toPaginate } from '@/utils/infrastructure';

const TOKEN_STORAGE_KEY = `x-${window.tabID}-list-token`;

const readCachedToken = () => {
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    const parsed = isJsonLike(raw) ? JSON.parse(raw) : null;
    return parsed?.token ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedToken = (tokenData) => {
  if (!tokenData) return;
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
  } catch {
    // Silent fail
  }
};

const api = bindServices('listBuilder');

function* getTokenContext(select) {
  const tk = yield select((s) => s.listBuilder.generatedToken || {});
  return { customListId: tk.customListId, versionId: tk.versionId, token: tk.token };
}

const paginationMeta = { page: 1, limit: 5, count: 0, totalPages: 0 };

// Expand the navigation properties that the settings panel needs so the
// labels (group, process, dynamic entity) render with names, not raw GUIDs.
const customListExpand = {
  customListGroup: { select: 'id,name' },
  process: { select: 'id,name' },
  dynamicEntity: { select: 'id,displayName' },
};

const initialState = {
  currentList: null,
  fields: [],
  configurations: null,
  generatedToken: null,
  versions: [],
  dependencies: [],
  traces: [],
  tracesPagination: paginationMeta,
  tracesOrderBy: 'created desc',
  filtersConfig: [],
  loading: false,
  initError: null,
  // Collaborators (CustomList.config/collaborators) — mirrors the
  // entity-builder pattern. The drawer in the list card consumes this
  // slice via dva/connect.
  collaborators: [],
};

const Model = {
  namespace: 'listBuilder',
  state: initialState,

  effects: {
    *initBuilder({ payload }, { call, put }) {
      const { listId } = payload;
      yield put({ type: 'setLoading', payload: true });

      let list;
      try {
        list = yield call(api.getById, {
          route: 'customList',
          id: listId,
          expand: customListExpand,
        });
        if (!list || !list.id) throw new Error('not-found');
        yield put({ type: 'saveList', payload: list });
      } catch (e) {
        yield put({ type: 'setLoading', payload: false });
        yield put({ type: 'initFailed', payload: 'list' });
        return;
      }

      try {
        const versionsRes = yield call(api.apiRoute, {
          route: `customList/config/${listId}/versions`,
          params: {
            select:
              'id,customListId,number,current,published,publisher,publishedDate,creator,created',
            orderBy: 'number desc',
            limit: 50,
          },
        });
        const items = versionsRes?.items || versionsRes?.data?.items || [];
        yield put({ type: 'saveVersions', payload: items });
      } catch (e) {
        // non-critical
      }

      try {
        const ids = [listId, CoreEnums.AccessMode.Write].filter(Boolean).join('/');
        const tokenRes = yield call(api.apiRoute, {
          route: `customList/config/generateToken/${ids}`,
          method: 'post',
        });

        if (!tokenRes) throw new Error('not-found');
        writeCachedToken(tokenRes);

        yield put({ type: 'saveToken', payload: tokenRes });

        if (tokenRes?.customListId && tokenRes?.versionId) {
          yield put({
            type: 'fetchFields',
            payload: {
              listId: tokenRes.customListId,
              versionId: tokenRes.versionId,
              token: tokenRes.token,
            },
          });
        }
      } catch (e) {
        yield put({ type: 'setLoading', payload: false });
        yield put({ type: 'initFailed', payload: 'token' });
        return;
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },

    *fetchVersions({ payload }, { call, put, select }) {
      try {
        const listId = payload?.listId || (yield select((s) => s.listBuilder.currentList?.id));
        if (!listId) return;
        const res = yield call(api.apiRoute, {
          route: `customList/config/${listId}/versions`,
          params: {
            select:
              'id,customListId,number,current,published,publisher,publishedDate,creator,created',
            orderBy: 'number desc',
            limit: 50,
          },
        });
        const items = res?.items || res?.data?.items || [];
        yield put({ type: 'saveVersions', payload: items });
      } catch (e) {
        // handled globally
      }
    },

    *changeVersion({ payload }, { call, put, select }) {
      const { versionId } = payload;
      if (!versionId) return;

      const listId = yield select((s) => s.listBuilder.currentList?.id);
      const versions = yield select((s) => s.listBuilder.versions);
      if (!listId) return;

      const selected = versions.find((v) => v.id === versionId);
      const mode = selected?.published ? CoreEnums.AccessMode.ReadOnly : CoreEnums.AccessMode.Write;

      yield put({ type: 'setLoading', payload: true });

      try {
        const tokenRes = yield call(api.apiRoute, {
          route: `customList/config/generateToken/${listId}/${mode}/${versionId}`,
          method: 'post',
        });

        writeCachedToken(tokenRes);
        yield put({ type: 'saveToken', payload: tokenRes });

        if (tokenRes?.customListId && tokenRes?.versionId) {
          yield put({
            type: 'fetchFields',
            payload: {
              listId: tokenRes.customListId,
              versionId: tokenRes.versionId,
              token: tokenRes.token,
            },
          });
        }
      } catch (e) {
        // handled globally
      } finally {
        yield put({ type: 'setLoading', payload: false });
      }
    },

    *fetchFields({ payload }, { call, put, select }) {
      try {
        const token = payload?.token || (yield select((s) => s.listBuilder.generatedToken?.token));
        const listId =
          payload?.listId || (yield select((s) => s.listBuilder.generatedToken?.customListId));
        const versionId =
          payload?.versionId || (yield select((s) => s.listBuilder.generatedToken?.versionId));

        const res = yield call(api.getAll, {
          route: `customList/config/version/${listId}/${versionId}`,
          params: { token },
        });

        const config = res?.configurations || {};
        yield put({ type: 'saveFields', payload: config.fields || [] });
        yield put({ type: 'saveConfigurations', payload: config });
      } catch (e) {
        yield put({ type: 'saveFields', payload: [] });
        yield put({ type: 'saveConfigurations', payload: null });
      }
    },

    *fetchDependencies({ payload }, { call, put, select }) {
      try {
        const listId = payload?.listId || (yield select((s) => s.listBuilder.currentList?.id));
        if (!listId) return;
        const { versionId, token } = yield select((s) => s.listBuilder.generatedToken || {});
        const vid = payload?.versionId || versionId;
        const route = vid
          ? `customList/config/${listId}/dependencies/${vid}`
          : `customList/config/${listId}/dependencies`;
        const res = yield call(api.apiRoute, { route, params: { limit: 1000, token } });
        yield put({ type: 'saveDependencies', payload: res?.items || res || [] });
      } catch (e) {
        yield put({ type: 'saveDependencies', payload: [] });
      }
    },

    *fetchTraces({ payload }, { call, put, select }) {
      try {
        const listId = payload?.id || (yield select((s) => s.listBuilder.currentList?.id));
        if (!listId) return;
        const { versionId, token } = yield select((s) => s.listBuilder.generatedToken || {});
        const vid = payload?.versionId || versionId;
        const route = vid
          ? `customList/config/traces/${listId}/${vid}`
          : `customList/config/traces/${listId}`;
        const res = yield call(api.apiRoute, {
          route,
          params: {
            token,
            limit: payload?.limit || 20,
            page: payload?.page || 1,
            ...(payload?.orderBy && { orderBy: payload.orderBy }),
            ...(payload?.search && { search: payload.search, searchSelect: payload.searchSelect }),
            ...(payload?.filter && { filter: payload.filter }),
          },
        });
        yield put({
          type: 'queryTraces',
          payload: { ...res, ...payload },
        });
      } catch (e) {
        yield put({ type: 'queryTraces', payload: { items: [], page: 1, limit: 20 } });
      }
    },

    // --- Generic builder operations (correct API routes) ---

    *appendElement({ payload }, { call, select }) {
      const { elementType, data } = payload;
      const { customListId, versionId, token } = yield* getTokenContext(select);
      if (!customListId || !versionId || !token) return null;
      const res = yield call(api.apiRoute, {
        route: `customList/config/append/${customListId}/${versionId}`,
        params: { token },
        method: 'post',
        data: { elementType, data },
      });
      return res;
    },

    *modifyElement({ payload }, { call, select }) {
      const { elementType, itemId, data } = payload;
      const { customListId, versionId, token } = yield* getTokenContext(select);
      if (!customListId || !versionId || !token) return null;
      const res = yield call(api.apiRoute, {
        route: `customList/config/modify/${customListId}/${versionId}`,
        params: { token },
        method: 'post',
        data: { elementType, itemId, data },
      });
      return res;
    },

    *removeElement({ payload }, { call, select }) {
      const { elementType, itemId, itemKey, force } = payload;
      const { customListId, versionId, token } = yield* getTokenContext(select);
      if (!customListId || !versionId || !token) return null;
      const body = { elementType, itemId };
      if (itemKey !== undefined) body.itemKey = itemKey;
      if (force !== undefined) body.force = force;
      const res = yield call(api.apiRoute, {
        route: `customList/config/remove/${customListId}/${versionId}`,
        params: { token },
        method: 'post',
        data: body,
      });
      return res;
    },

    // --- Field wrappers using correct API routes ---

    *reorderFields({ payload }, { call, put, select }) {
      const { orderedIds } = payload;
      if (!orderedIds?.length) return;

      const { customListId, versionId, token } = yield* getTokenContext(select);
      if (!customListId || !versionId || !token) return;

      const fields = yield select((s) => s.listBuilder.fields);
      const idOrder = {};
      orderedIds.forEach((id, i) => {
        idOrder[id] = i;
      });
      const reordered = [...fields]
        .sort((a, b) => (idOrder[a.id] ?? 999) - (idOrder[b.id] ?? 999))
        .map((f, i) => ({ ...f, order: i }));
      yield put({ type: 'saveFields', payload: reordered });

      try {
        yield call(api.apiRoute, {
          route: `customList/config/modify/${customListId}/${versionId}`,
          params: { token },
          method: 'post',
          data: { elementType: 'fields-order', data: { orderedIds } },
        });
      } catch (e) {
        yield put({ type: 'saveFields', payload: fields });
      }
    },

    *addField({ payload }, { call, put, select }) {
      const { data } = payload;
      const { customListId, versionId, token } = yield* getTokenContext(select);
      if (!customListId || !versionId || !token) return;
      try {
        yield call(api.apiRoute, {
          route: `customList/config/append/${customListId}/${versionId}`,
          params: { token },
          method: 'post',
          data: { elementType: 'field', data },
        });
      } catch (e) {
        // handled globally
      }
    },

    *modifyField({ payload }, { call, put, select }) {
      const { fieldId, data } = payload;
      const { customListId, versionId, token } = yield* getTokenContext(select);
      if (!customListId || !versionId || !token) return;
      try {
        yield call(api.apiRoute, {
          route: `customList/config/modify/${customListId}/${versionId}`,
          params: { token },
          method: 'post',
          data: { elementType: 'field', itemId: fieldId, data },
        });
      } catch (e) {
        // handled globally
      }
    },

    *deleteField({ payload }, { call, put, select }) {
      const { fieldId } = payload;
      const { customListId, versionId, token } = yield* getTokenContext(select);
      if (!customListId || !versionId || !token || !fieldId) return;
      try {
        yield call(api.apiRoute, {
          route: `customList/config/remove/${customListId}/${versionId}`,
          params: { token },
          method: 'post',
          data: { elementType: 'field', itemId: fieldId },
        });
      } catch (e) {
        // handled globally
      }
    },

    *refreshList(_, { call, put, select }) {
      try {
        const listId = yield select((s) => s.listBuilder.currentList?.id);
        if (!listId) return;
        const list = yield call(api.getById, {
          route: 'customList',
          id: listId,
          expand: customListExpand,
        });
        yield put({ type: 'saveList', payload: list });
      } catch (e) {
        // handled globally
      }
    },

    *updateList({ payload }, { call, put, select }) {
      const targetId = payload?.id || (yield select((s) => s.listBuilder.currentList?.id));
      if (!targetId) return;

      // PUT customList expects the full entity body with id in the payload
      // (per backend docs: "Actualizar entidad completa").
      try {
        yield call(api.update, { route: 'customList', data: { ...payload, id: targetId } });
        message.success(getIntl().formatMessage({ id: 'listBuilder.messages.saved' }));
        yield put({ type: 'refreshList' });
      } catch (e) {
        // handled globally
      }
    },

    *publishList(_, { call, put, select }) {
      try {
        const { customListId, versionId, token, mode } = yield select(
          (s) => s.listBuilder.generatedToken,
        );
        if (mode !== CoreEnums.AccessMode.Write) return;

        const raw = yield call(api.apiRoute, {
          route: `customList/config/publish/${customListId}/${versionId}`,
          params: { token },
          method: 'post',
        });
        const res = raw?.data || raw;

        const translate = getIntl().formatMessage;
        message.success(res?.message || translate({ id: 'listBuilder.publish.success' }));

        let writeToken = yield call(api.apiRoute, {
          route: `customList/config/generateToken/${customListId}/${CoreEnums.AccessMode.Write}`,
          method: 'post',
        });

        if (
          !writeToken ||
          writeToken.versionId === versionId ||
          writeToken.mode !== CoreEnums.AccessMode.Write
        ) {
          yield call(() => new Promise((r) => setTimeout(r, 1500)));
          writeToken = yield call(api.apiRoute, {
            route: `customList/config/generateToken/${customListId}/${CoreEnums.AccessMode.Write}`,
            method: 'post',
          });
        }

        if (writeToken) {
          writeCachedToken(writeToken);
          yield put({ type: 'saveToken', payload: writeToken });

          yield put({ type: 'fetchVersions', payload: { listId: customListId } });
          yield put({
            type: 'fetchFields',
            payload: {
              listId: writeToken.customListId,
              versionId: writeToken.versionId,
              token: writeToken.token,
            },
          });
          yield put({
            type: 'fetchDependencies',
            payload: { listId: customListId, versionId: writeToken.versionId },
          });
          yield put({ type: 'refreshList' });
        }
      } catch (e) {
        const errorMsg = e?.message || e?.data?.message || e?.Message;
        if (errorMsg) message.error(errorMsg);
      }
    },

    // Access control (`accessControl`) is managed via the generic builder
    // operations: callers dispatch
    //   appendElement({ elementType: 'access-control', data: { ... } })
    // followed by `fetchFields` to refresh `configurations.accessControl`.
    // See AccessControlDrawer / _accessControlForm.js for the payload
    // shape (matches ConfigCustomListAccessControl on the backend).

    // ─── Collaborators (CustomList domain) ───────────────────────────
    //
    // Backend contract (see "AccessControl vs Collaborator" UX guide):
    //   GET    /customList/config/collaborators/{customListId}
    //   POST   /customList/config/collaborators/{customListId} body { collaboratorId }
    //   DELETE /customList/config/collaborators/{customListId}/{collaboratorRecordId}
    //
    // Each effect tolerates the token being absent so the drawer can
    // still render the current list while a write token is being
    // issued. Refresh contract: every mutation triggers a single
    // inline GET against the primary replica (`readPriority: true`)
    // so the drawer and the row-command kebab see the row we just
    // POSTed/DELETEd without waiting for the read replica to catch up.
    // ─────────────────────────────────────────────────────────────────

    *fetchCollaborators({ payload }, { call, put, select }) {
      try {
        const customListId =
          payload?.customListId ||
          (yield select((s) => s.listBuilder.generatedToken?.customListId));
        if (!customListId) {
          yield put({ type: 'saveCollaborators', payload: [] });
          return;
        }
        const token = yield select((s) => s.listBuilder.generatedToken?.token);
        const res = yield call(api.getAll, {
          route: `customList/config/collaborators/${customListId}`,
          params: { token },
          ...(payload?.readPriority && { readPriority: true }),
        });
        const raw = res?.items || res?.data?.items || res || [];
        const list = Array.isArray(raw) ? raw : [];
        // Single batched `user/paged` call keeps the GET contract
        // minimal (no $expand assumed) and works regardless of which
        // navigation property the backend uses for the user join
        // (`collaborator` vs `collaboratorUser`).
        const hydrated = yield call(hydrateCollaboratorUsers, list);
        yield put({ type: 'saveCollaborators', payload: hydrated });
      } catch (e) {
        yield put({ type: 'saveCollaborators', payload: [] });
      }
    },

    *addCollaborator({ payload }, { call, put, select }) {
      try {
        const { customListId, token, mode } = yield select(
          (s) => s.listBuilder.generatedToken || {},
        );
        if (customListId && mode === CoreEnums.AccessMode.Write && payload?.collaboratorId) {
          yield call(api.create, {
            route: `customList/config/collaborators/${customListId}`,
            params: { token },
            // Per the UX guide the POST body for CustomList uses
            // `collaboratorId` (mirrors App/AIAgent — NOT
            // `collaboratorUserId` like DynamicEntity).
            data: { collaboratorId: payload.collaboratorId },
          });
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
      }
      yield put({ type: 'fetchCollaborators', payload: { readPriority: true } });
    },

    *removeCollaborator({ payload }, { call, put, select }) {
      try {
        const { customListId, token, mode } = yield select(
          (s) => s.listBuilder.generatedToken || {},
        );
        if (customListId && payload?.id && mode === CoreEnums.AccessMode.Write) {
          yield call(api.apiRoute, {
            route: `customList/config/collaborators/${customListId}/${payload.id}`,
            method: 'delete',
            params: { token },
          });
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
      yield put({ type: 'fetchCollaborators', payload: { readPriority: true } });
    },
  },

  reducers: {
    saveList(state, { payload }) {
      return { ...state, currentList: payload };
    },
    saveToken(state, { payload }) {
      return { ...state, generatedToken: payload };
    },
    saveFields(state, { payload }) {
      return { ...state, fields: payload };
    },
    saveVersions(state, { payload }) {
      return { ...state, versions: payload };
    },
    saveConfigurations(state, { payload }) {
      return { ...state, configurations: payload };
    },
    saveDependencies(state, { payload }) {
      return { ...state, dependencies: payload };
    },
    queryTraces(state, { payload }) {
      return {
        ...state,
        traces: payload.items || [],
        tracesPagination: {
          ...state.tracesPagination,
          ...payload,
          ...toPaginate(payload),
        },
      };
    },
    queryOrderBy(state, { payload }) {
      return { ...state, tracesOrderBy: payload || 'created desc' };
    },
    filtersConfig(state, { payload }) {
      return { ...state, filtersConfig: payload };
    },
    setLoading(state, { payload }) {
      return { ...state, loading: payload };
    },
    initFailed(state, { payload }) {
      return { ...state, initError: payload };
    },
    saveCollaborators(state, { payload }) {
      return { ...state, collaborators: payload || [] };
    },
    resetBuilder() {
      return initialState;
    },
  },
};

export default Model;
